<script lang="ts" setup>
import { computed, onMounted, ref } from "vue";
// import { CurrentInvitationStore } from "../../stores/CurrentInvtationStore";
import { appContainer } from "../../container";
import { LoadingService } from "../../services/loadingService";
import { ManageQuestionService } from "../../services/manageQuestionService";
import { PagingInterface } from "../../interface/PaginationInterface";
import {
  QuestionInputTypes,
  QuestionResponse,
  QuestionResponseSubCategories,
  ResponseInterFace,
} from "../../interface/QuestionInterface";
import RaterQuestionComponent from "./RaterQuestionComponent.vue";
import { ToastService } from "../../services/toastService";
import FinishFeedbackModal from "./FinishFeedbackModal.vue";
import router from "../../routes";
import { AllRaterType } from "../../interface/UserInterface";
import { useUserStore } from "../../stores/UserStore";
import {
  FeedbackPage,
  buildPages,
  categoryOfSubcategory,
  isCompanyResponse,
  isParagraphResponse,
  pageIndexFor,
  paragraphCategory,
} from "./feedbackPages";

// const currentInvitationStore = CurrentInvitationStore();
const userStore = useUserStore();
const loadingService = appContainer.resolve(LoadingService);
const questionService = appContainer.resolve(ManageQuestionService);
const toastService = appContainer.resolve(ToastService);

const paginatedQuestionResponse = ref<PagingInterface<QuestionResponse>>();
const questionResponses = ref<QuestionResponse[]>([]);
const responseArray = ref<ResponseInterFace[]>([]);
const pages = ref<FeedbackPage[]>([]);
const currentIndex = ref(0);
const currentPage = computed<FeedbackPage | undefined>(
  () => pages.value[currentIndex.value],
);
const isLastPage = computed(
  () => currentIndex.value === pages.value.length - 1,
);
const feedbackModal = ref(false);
const isLoading = ref(true);

onMounted(async () => {
  const loader = await loadingService.show();
  await userStore.getRater();
  try {
    const allResponses = await questionService.getFeedbackResponse({
      invitationId: userStore.invitation!.id,
      all: true,
    });
    pages.value = buildPages(allResponses.items);
    const firstEmptyResponse = await questionService.getCurrentPage(
      userStore.invitation!.id,
    );
    currentIndex.value = pageIndexFor(firstEmptyResponse, pages.value);
    await loadPage();
  } catch {
    toastService.error("Something went wrong");
  } finally {
    isLoading.value = false;
    loader.hide();
  }
});

async function loadPage(): Promise<void> {
  const page = currentPage.value;
  if (!page) {
    questionResponses.value = [];
    return;
  }
  const loader = await loadingService.show();
  try {
    const result = await questionService.getFeedbackResponse({
      invitationId: userStore.invitation!.id,
      all: true,
      ...(page.type === "rating" && {
        subcategory: page.subcategory,
        inputType: QuestionInputTypes.Rating,
      }),
      // company page fetches rating and paragraph questions together
      ...(page.type === "company" && {
        subcategory: QuestionResponseSubCategories.Custom,
      }),
      ...(page.type === "comments" && {
        inputType: QuestionInputTypes.Paragraph,
        ...(page.category && { category: page.category }),
      }),
    });

    if (page.type === "comments") {
      result.items = result.items
        .filter(
          (item) =>
            isParagraphResponse(item) &&
            !isCompanyResponse(item) &&
            paragraphCategory(item) === page.category,
        )
        .sort((a, b) => a.question.id - b.question.id);
    }
    if (page.type === "company") {
      const ratingQuestions = result.items.filter(
        (item) => !isParagraphResponse(item),
      );
      const paragraphQuestions = result.items.filter(isParagraphResponse);
      result.items = [...ratingQuestions, ...paragraphQuestions];
    }
    paginatedQuestionResponse.value = result;
    questionResponses.value = result.items;
  } finally {
    loader.hide();
  }
}

function handleCurrentResponse(response: ResponseInterFace) {
  if (!responseArray.value.find((item) => item.id === response.id)) {
    responseArray.value.push(response);
  }
}

/**
 * Saves the current page and moves forward; returns false when it could not be saved
 */
async function submitResponses(): Promise<boolean> {
  if (
    currentPage.value?.type !== "comments" &&
    responseArray.value.filter((response) => {
      // Questions flagged optional may be left blank without blocking submit
      const questionResponse = questionResponses.value.find(
        (item) => item.id === response.id,
      );
      if (questionResponse?.question.optional) {
        return false;
      }
      return (
        response.rating_response === null &&
        (!response.paragraph_response ||
          response.paragraph_response.trim() === "")
      );
    }).length >= 1
  ) {
    toastService.error("Please Provide Rating for All Questions");
    return false;
  }
  const loader = await loadingService.show();
  try {
    (document.querySelector(".app-page") as HTMLElement).scrollTop = 0;
    await questionService.submitResponse(
      responseArray.value,
      userStore.invitation!.id,
    );
    if (!isLastPage.value) {
      responseArray.value = [];
      currentIndex.value += 1;
      await loadPage();
    }
    return true;
  } catch (error) {
    toastService.error("Error submitting responses");
    return false;
  } finally {
    loader.hide();
  }
}

async function handleBackBtn() {
  if (currentIndex.value === 0) {
    return;
  }
  responseArray.value = [];
  currentIndex.value -= 1;
  await loadPage();
}

async function handleFinishFeedback() {
  if (await submitResponses()) {
    feedbackModal.value = true;
  }
}
</script>

<template>
  <div v-if="!isLoading" class="fit-height d-flex flex-column">
    <div class="question-header" v-if="currentPage?.type !== 'comments'">
      <strong class="fs-5">Questions</strong>
      <p class="mt-2">
        Please select the number that most accurately describes
        <strong>
          {{
            userStore.invitation?.rater_type === AllRaterType.Self
              ? "You"
              : `${userStore.invitation?.invited_for_user?.first_name} ${userStore.invitation?.invited_for_user?.last_name}`
          }}
        </strong>
      </p>
    </div>
    <div v-else class="question-header mb-3">
      <strong class="fs-5">Written Comments</strong>
    </div>
    <div
      class="d-flex align-items-start flex-column"
      :class="{ ' pb-3': currentPage?.type !== 'comments' }"
    >
      <button
        class="btn btn-primary text-white text-white"
        type="button"
        @click="handleBackBtn"
        :disabled="currentIndex === 0"
      >
        Back
      </button>
      <div class="mx-auto" v-if="currentPage?.type === 'rating'">
        <h3 class="ff-montserrat mb-0 text-primary text-center">
          {{ categoryOfSubcategory(currentPage.subcategory) }}
        </h3>
        <h6 class="ff-montserrat mb-0 grey-color text-center">
          {{ currentPage.subcategory }}
        </h6>
      </div>
      <div
        class="mx-auto"
        v-if="currentPage?.type === 'comments' && currentPage.category"
      >
        <h3 class="ff-montserrat mb-0 text-primary text-center">
          {{ currentPage.category }}
        </h3>
        <h6 class="ff-montserrat mb-0 grey-color text-center">
          Written Comments
        </h6>
      </div>
      <div v-if="currentPage?.type === 'company'" class="mx-auto">
        <h3 class="ff-montserrat mb-0 text-primary text-center">
          Organization
        </h3>
        <h6 class="ff-montserrat mb-0 grey-color text-center">
          Values/Priorities
        </h6>
      </div>
    </div>

    <!-- Company Message Display -->
    <div
      v-if="
        currentPage?.type === 'company' &&
        userStore.invitation?.company?.description
      "
      class="company-message-box p-4"
    >
      <h5 class="fw-bold mb-3">Company Message:</h5>
      <p class="mb-0">{{ userStore.invitation.company.description }}</p>
    </div>

    <div
      class="d-flex align-items-start mb-sm-5 flex-column"
      :class="{ 'mb-sm-5 pb-5': currentPage?.type !== 'comments' }"
    >
      <!-- <button
        v-if="isLastPage"
        class="btn btn-primary text-white"
        type="button"
        @click="handleFinishFeedback"
      >
        Finish
      </button>
      <button
        v-else
        class="btn btn-primary text-white"
        type="button"
        @click="submitResponses"
      >
        Continue
      </button> -->
    </div>

    <form class="d-flex flex-column" @submit.prevent="submitResponses">
      <RaterQuestionComponent
        v-for="(questionResponse, index) in questionResponses"
        :key="questionResponse.id"
        :question="questionResponse.question"
        :index="
          paginatedQuestionResponse
            ? (paginatedQuestionResponse?.page - 1) *
                paginatedQuestionResponse.offset +
              (index + 1)
            : 1
        "
        :currentResponse="questionResponse"
        @updateResponse="handleCurrentResponse"
      />
    </form>

    <div class="d-flex align-items-center my-md-5 my-4">
      <!-- <button
        class="btn btn-primary text-white text-white"
        type="button"
        @click="handleBackBtn"
        :disabled="currentIndex === 0"
      >
        Back
      </button> -->
      <div
        class="d-flex align-items-center justify-content-center w-100 page-footer py-3"
      >
        <span>Page {{ currentIndex + 1 }} of {{ pages.length }}</span>
      </div>
      <button
        v-if="isLastPage"
        class="btn btn-primary text-white text-nowrap"
        type="button"
        @click="handleFinishFeedback"
      >
        Save and Finish
      </button>
      <button
        v-else
        class="btn btn-primary text-white"
        type="button"
        @click="submitResponses"
      >
        Continue
      </button>
    </div>
  </div>
  <FinishFeedbackModal
    v-if="feedbackModal"
    @close="feedbackModal = false"
    @submitted="router.push('/thank-you')"
  />
</template>

<style lang="scss" scoped>
.fit-height {
  min-height: calc(100vh - 110px);
  overflow: auto;
}

.grey-color {
  color: #999;
}

.company-message-box {
  background-color: #eaf3fe;
  border-radius: 8px;
  border-left: 4px solid #0d6efd;
}

.rating-heading {
  display: grid;
  grid-template-columns: repeat(12, 1fr);
  gap: 10px;

  span {
    width: 110px;
    font-size: 14px;
    font-weight: 600;
    text-wrap: nowrap;
    text-align: center;
  }
}

.start-6 {
  grid-column: 6;
}

.page-footer {
  .btn {
    min-width: 100px;
  }
}

@media (max-width: 992px) {
  form {
    height: 100%;
  }

  h3 {
    font-size: 20px;
  }

  h6 {
    font-size: 14px;
  }

  button {
    padding: 6px;
    font-size: 14px;
  }
}
</style>
